import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLocation as useAppLocation } from '../../contexts/LocationContext';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import './CalculatorPage.css';

const IFRAME_LOAD_TIMEOUT_MS = 12000;
const CALCULATOR_HEIGHT_MESSAGE = 'calculator-height';
const IFRAME_MIN_HEIGHT_PX = 900;
const IFRAME_MAX_HEIGHT_PX = 4000;
/** Extra height so the iframe doesn't clip the bottom (e.g. Next button, chart labels) */
const IFRAME_HEIGHT_BUFFER_PX = 140;

/**
 * Calculator tab page: embeds the CoolClimate calculator (new Next.js app) in an iframe.
 * Only visible to developers and super admins (not local government admins). URL is configurable via REACT_APP_CALCULATOR_URL.
 * When a ClimatePlans location is selected, passes ?location= and ?geofips= so the calculator pre-fills "Davis, California" and loads that place's defaults (geofips avoids leading-zero and name ambiguity).
 */
export const CalculatorPage: React.FC = () => {
  const { locationSlug } = useParams<{ locationSlug: string }>();
  const { selectedLocation, selectedGeofips, selectLocation, getGeofipsForLocation, locationData } =
    useAppLocation();
  // Re-run when slug or location catalog loads (peek reads service catalog; tie memo to catalog size).
  const locationCatalog = locationData?.availableLocations;
  const locationFromSlug = useMemo(() => {
    if (!locationSlug) return '';
    // Invalidate when catalog hydrates (length changes) without relying on eslint-disable.
    void locationCatalog?.length;
    return supabaseLocationService.peekLocationBySlug(locationSlug)?.name ?? '';
  }, [locationSlug, locationCatalog]);
  const effectiveLocation = locationFromSlug || selectedLocation;
  const geofipsFromContext =
    locationFromSlug && selectedLocation && locationFromSlug !== selectedLocation
      ? null
      : selectedGeofips;
  const [resolvedGeofips, setResolvedGeofips] = useState<number | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);

  // Resolve geofips when we have a location but context hasn't set selectedGeofips yet (e.g. direct nav to calculator tab)
  const geofipsForUrl = geofipsFromContext ?? resolvedGeofips;
  const [geofipsTimedOut, setGeofipsTimedOut] = useState(false);

  // Keep context synchronized with slugged URLs like /pacific-grove/coolclimate-calculator.
  useEffect(() => {
    if (!locationFromSlug) return;
    if (selectedLocation === locationFromSlug) return;
    void selectLocation(locationFromSlug);
  }, [locationFromSlug, selectedLocation, selectLocation]);

  useEffect(() => {
    if (!effectiveLocation?.trim() || geofipsFromContext != null) {
      setResolvedGeofips(null);
      setGeofipsTimedOut(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      getGeofipsForLocation(effectiveLocation).then((g) => {
        if (!cancelled && g != null) setResolvedGeofips(g);
      });
    }, 0);
    const timeout = setTimeout(() => {
      if (!cancelled && (geofipsFromContext ?? resolvedGeofips) == null) setGeofipsTimedOut(true);
    }, 2500);
    return () => {
      cancelled = true;
      clearTimeout(t);
      clearTimeout(timeout);
    };
    // resolvedGeofips intentionally omitted: effect runs only when location/selection changes, not when resolution completes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLocation, geofipsFromContext, getGeofipsForLocation]);

  const isLocalhostHost = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '::1'
    );
  }, []);

  // Env wins; else local dev → localhost:3001; else production/staging → same-origin /calculator (Apache proxy).
  // If .env bakes in http://localhost:3001 for production builds, ignore it and use same-origin /calculator.
  const baseUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const fallbackLocal = isLocalhostHost ? 'http://localhost:3001' : '';
    const fallbackSameOriginCalculator =
      !isLocalhostHost && origin ? `${origin}/calculator` : '';

    let fromEnv = process.env.REACT_APP_CALCULATOR_URL?.trim() || '';
    if (fromEnv) {
      try {
        const abs =
          fromEnv.startsWith('/') && origin
            ? new URL(fromEnv, origin).href
            : new URL(fromEnv).href;
        const parsed = new URL(abs);
        const isLocalCalculatorHost =
          parsed.hostname === 'localhost' ||
          parsed.hostname === '127.0.0.1' ||
          parsed.hostname === '::1';
        if (isLocalCalculatorHost && !isLocalhostHost) {
          fromEnv = '';
        } else {
          fromEnv = abs;
        }
      } catch {
        fromEnv = '';
      }
    }

    const candidate = fromEnv || fallbackLocal || fallbackSameOriginCalculator;
    if (!candidate) return '';

    try {
      return new URL(candidate).href;
    } catch {
      return fallbackSameOriginCalculator || fallbackLocal || '';
    }
  }, [isLocalhostHost]);

  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'pre-fix',hypothesisId:'H1',location:'CalculatorPage.tsx:baseUrl',message:'Calculator base URL resolved',data:{baseUrl,isLocalhostHost,hostname:typeof window!=='undefined'?window.location.hostname:null,port:typeof window!=='undefined'?window.location.port:null,protocol:typeof window!=='undefined'?window.location.protocol:null},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
  }, [baseUrl, isLocalhostHost]);

  // Pass location + geofips when we have geofips; after 2.5s without geofips still load with ?location= so calculator can try name search ("Berkeley, California").
  const calculatorSrc = useMemo(() => {
    if (!baseUrl) return '';
    if (!effectiveLocation?.trim()) return baseUrl;
    const geofips = geofipsForUrl != null && Number.isFinite(geofipsForUrl) ? geofipsForUrl : null;
    const params = new URLSearchParams();
    // Calculator expects "City, California" (not just "Berkeley") for valid lookups and defaults
    const isCalifornia = geofips != null && Math.floor(geofips / 100000) === 6;
    const locationParam =
      isCalifornia && !/,\s*(California|CA)\s*$/i.test(effectiveLocation.trim())
        ? effectiveLocation.trim() + ', California'
        : effectiveLocation.trim();
    params.set('location', locationParam);
    if (geofips != null) {
      const padded = String(geofips).padStart(7, '0').slice(-7);
      params.set('geofips', padded);
      if (typeof console !== 'undefined' && console.log) {
        console.log('[ClimatePlans] calculator geofips:', { raw: geofips, padded, location: locationParam });
      }
    }
    if (geofips == null && !geofipsTimedOut) return ''; // wait for geofips (or timeout) before loading
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${params.toString()}`;
  }, [baseUrl, effectiveLocation, geofipsForUrl, geofipsTimedOut]);

  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'pre-fix',hypothesisId:'H2',location:'CalculatorPage.tsx:calculatorSrc',message:'Calculator iframe src computed',data:{calculatorSrc,effectiveLocation,geofipsForUrl,geofipsTimedOut},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
  }, [calculatorSrc, effectiveLocation, geofipsForUrl, geofipsTimedOut]);

  // When iframe src changes, reset load state and height so we show spinner again
  useEffect(() => {
    if (!calculatorSrc) return;
    setIframeLoaded(false);
    setLoadTimedOut(false);
    setIframeHeight(null);
  }, [calculatorSrc]);

  // Listen for height from calculator so we can size the iframe and avoid clipping the chart
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === CALCULATOR_HEIGHT_MESSAGE && typeof event.data.height === 'number') {
        const contentHeight = event.data.height + IFRAME_HEIGHT_BUFFER_PX;
        const h = Math.min(IFRAME_MAX_HEIGHT_PX, Math.max(IFRAME_MIN_HEIGHT_PX, contentHeight));
        setIframeHeight(h);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // If the iframe never fires onLoad (e.g. calculator not running), stop spinner after timeout
  useEffect(() => {
    if (!calculatorSrc || iframeLoaded) return;
    const t = setTimeout(() => {
      setIframeLoaded(true);
      setLoadTimedOut(true);
      // #region agent log
      fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'pre-fix',hypothesisId:'H3',location:'CalculatorPage.tsx:iframeTimeout',message:'Calculator iframe load timed out',data:{calculatorSrc,timeoutMs:IFRAME_LOAD_TIMEOUT_MS},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
    }, IFRAME_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [calculatorSrc, iframeLoaded]);

  return (
    <div className="calculator-page">
      <div className="calculator-page-content">
        {!baseUrl ? (
          <div className="calculator-page-placeholder">
            <p>Calculator URL could not be determined.</p>
            <p>
              Set a valid <code>REACT_APP_CALCULATOR_URL</code> in <code>.env</code> and rebuild.
              On production, the app normally uses <code>/calculator</code> on this site (reverse proxy).
              For local dev, run the calculator (e.g. <code>cd coolclimate-calculator &amp;&amp; npm run dev:3001</code>); on localhost the app defaults to <code>http://localhost:3001</code>.
            </p>
          </div>
        ) : !calculatorSrc && effectiveLocation?.trim() ? (
          <div className="calculator-page-iframe-wrap" style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="calculator-page-loading">
              <div className="loading-spinner" />
              <p>Resolving location for {effectiveLocation}…</p>
            </div>
          </div>
        ) : (
          <div
            className="calculator-page-iframe-wrap"
            style={{
              height: iframeHeight != null ? `${iframeHeight}px` : undefined,
            }}
          >
            {!iframeLoaded && (
              <div className="calculator-page-loading">
                <div className="loading-spinner" />
                <p>Loading calculator…</p>
              </div>
            )}
            <iframe
              src={calculatorSrc}
              title="CoolClimate Calculator"
              className={`calculator-page-iframe ${iframeLoaded ? 'loaded' : ''}`}
              style={{
                height: iframeHeight != null ? `${iframeHeight}px` : '100%',
                minHeight: iframeHeight != null ? undefined : IFRAME_MIN_HEIGHT_PX,
              }}
              onLoad={() => {
                setIframeLoaded(true);
                setLoadTimedOut(false);
                // #region agent log
                fetch('http://127.0.0.1:7601/ingest/0966bb5c-6daa-4d7e-ba25-e84a55b66c1f',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'bdfc61'},body:JSON.stringify({sessionId:'bdfc61',runId:'pre-fix',hypothesisId:'H4',location:'CalculatorPage.tsx:iframeOnLoad',message:'Calculator iframe onLoad fired',data:{calculatorSrc},timestamp:Date.now()})}).catch(()=>{});
                // #endregion
              }}
            />
            {loadTimedOut && (
              <p className="calculator-page-timeout-hint">
                If the calculator is blank, from repo root start it in another terminal: <code>cd coolclimate-calculator &amp;&amp; npm run dev:3001</code>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
